import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, TextAreaField, SelectField, CurrencyField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import { usePropertyApiClient } from "../property/services/usePropertyApiClient";
import { createLeadService } from "./services/leadService";
import { MAX_DESCRIPTION_LENGTH } from "./constants/leadEndpoints";

const FORM_ID = "lead-form";
const LEADS_HOME = "/customer/leads";

// Same rules and messages as the mobile Add Lead form
const validationSchema = Yup.object({
  title: Yup.string().trim().min(5, "Title must be at least 5 characters").required("Title must be at least 5 characters"),
  description: Yup.string()
    .trim()
    .max(MAX_DESCRIPTION_LENGTH, `Description must be under ${MAX_DESCRIPTION_LENGTH} characters`)
    .required("Description is required"),
  budget: Yup.number()
    .typeError("Valid budget required")
    .min(0, "Valid budget required")
    .required("Valid budget required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  purposeId: Yup.string().required("Please select purpose"),
});

/** Lives inside <Formik> so it can read the form state for the unsaved-changes warning. */
const LeadFormBody = ({ isEdit, purposes, states, cities, onStateSelected, saved }) => {
  const { dirty, values, setFieldValue } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  const purposeOptions = useMemo(() => purposes.map((p) => ({ value: String(p.id), label: p.name })), [purposes]);
  const stateOptions = useMemo(() => states.map((s) => ({ value: s.name, label: s.name, id: s.id })), [states]);
  const cityOptions = useMemo(() => cities.map((c) => ({ value: c.name, label: c.name })), [cities]);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      <Section
        title="Requirement"
        hint={isEdit ? "Update your customer requirement anytime." : "Add your requirement so partners can connect with suitable properties."}
      >
        <div className="fk-grid">
          <TextField name="title" label="Title" required placeholder="Enter title" className="fk-col-full" />
          <SelectField name="purposeId" label="Purpose" required options={purposeOptions} placeholder="Select purpose" />
          <CurrencyField name="budget" label="Budget" required placeholder="Enter budget" />
        </div>
      </Section>

      <Section title="Location">
        <div className="fk-grid">
          <SelectField
            name="state"
            label="State"
            required
            options={stateOptions}
            placeholder="Select state"
            onSelect={(value, option) => {
              setFieldValue("city", "");
              onStateSelected(option.id);
            }}
          />
          <SelectField
            name="city"
            label="City"
            required
            options={cityOptions}
            disabled={!values.state}
            helper={!values.state ? "Select a state first" : undefined}
            placeholder="Select city"
          />
        </div>
      </Section>

      <Section title="Description">
        <TextAreaField
          name="description"
          label="Describe your requirement"
          required
          maxLength={MAX_DESCRIPTION_LENGTH}
          placeholder="What are you looking for?"
        />
      </Section>
    </Form>
  );
};

/** /customer/leads/new and /customer/leads/:Id/edit */
const LeadFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const navigate = useNavigate();
  const exit = useExitWizard(LEADS_HOME);

  const apiClient = usePropertyApiClient();
  const leadService = useMemo(() => createLeadService(apiClient), [apiClient]);

  const [purposes, setPurposes] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saved, setSaved] = useState(false);

  const loadCities = async (stateId) => {
    setCities(stateId ? await leadService.fetchCities(stateId).catch(() => []) : []);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [purposeList, stateList, leadData] = await Promise.all([
          leadService.fetchPurposes(),
          leadService.fetchStates(1).catch(() => []),
          isEdit ? leadService.getById(Id) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setPurposes(purposeList);
        setStates(stateList);
        const existing = leadData?.customerLead || null;
        setLead(existing);
        if (existing?.state) {
          const match = stateList.find((s) => s.name === existing.state);
          if (match) await loadCities(match.id);
        }
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.text || err?.message || "Unable to load the form.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id, leadService]);

  const initialValues = {
    title: lead?.title || "",
    description: lead?.description || "",
    budget: lead?.budget != null ? String(lead.budget) : "",
    state: lead?.state || "",
    city: lead?.city || "",
    purposeId: lead?.purpose?.id ? String(lead.purpose.id) : "",
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await leadService.save({
        id: isEdit ? Id : undefined,
        title: values.title.trim(),
        description: values.description.trim(),
        budget: values.budget,
        city: values.city,
        state: values.state,
        purposeId: values.purposeId,
      });
      showToast(isEdit ? "Lead updated successfully" : "Lead added successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(() => navigate(LEADS_HOME), 0);
    } catch (err) {
      // The form keeps its values so nothing is lost
      showToast(err?.response?.data?.text || err?.message || "Unable to save lead", { type: "error" });
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormPageLayout
      title={isEdit ? "Edit Lead" : "Create Lead"}
      subtitle={isEdit ? "Update your customer requirement." : "Share your requirement so partners can reach out."}
      formId={FORM_ID}
      submitLabel={isEdit ? "Update Lead" : "Add Lead"}
      submitting={submitting}
      loading={loading}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <LeadFormBody
          isEdit={isEdit}
          purposes={purposes}
          states={states}
          cities={cities}
          onStateSelected={loadCities}
          saved={saved}
        />
      </Formik>
    </FormPageLayout>
  );
};

export default LeadFormPage;
